import { PageLayout, ScreenCard } from '@/components/layout';
import { IconButton, Link, Typography } from '@/primer/components';

export const InviteErrorVariant = {
  BAD_REQUEST: 'bad-request',
  GENERIC: 'generic',
  SIGN_IN_FAILED: 'sign-in-failed',
} as const;

export type InviteErrorVariant = (typeof InviteErrorVariant)[keyof typeof InviteErrorVariant];

export interface InviteErrorScreenProps {
  variant: InviteErrorVariant;
  supportHref?: string;
  /** Shows a back control above the card that returns the creator to the previous step. */
  onBack?: () => void;
}

const variantContent: Record<
  InviteErrorVariant,
  {
    title: string;
    illustrationSrc: string;
    supportPrefix: string;
    supportSuffix?: string;
  }
> = {
  [InviteErrorVariant.BAD_REQUEST]: {
    title: 'Something went wrong.',
    illustrationSrc: '/assets/images/illustrations/fallback.svg',
    supportPrefix: '',
    supportSuffix: ' if you think this is a mistake.',
  },
  [InviteErrorVariant.SIGN_IN_FAILED]: {
    title: "We're unable to complete your sign-in.",
    illustrationSrc: '/assets/images/illustrations/bad-request.svg',
    supportPrefix: '',
    supportSuffix: ' if you think this is a mistake.',
  },
  [InviteErrorVariant.GENERIC]: {
    title: "There's a problem with your invitation.",
    illustrationSrc: '/assets/images/illustrations/generic-error.svg',
    supportPrefix: 'Please use the link from your email or ',
  },
};

/** Full-screen error treatment for failures in the public creator invite flow. */
export function InviteErrorScreen({
  variant,
  supportHref = 'mailto:creators@influenster.com',
  onBack,
}: InviteErrorScreenProps) {
  const content = variantContent[variant];

  return (
    <PageLayout className="flex flex-1 flex-col" gridProps={{ className: 'flex-1 px-5' }}>
      {onBack && (
        <div className="mb-4">
          <IconButton icon="chevron-left" aria-label="Back" onClick={() => onBack()} />
        </div>
      )}
      <ScreenCard shadow="second">
        <div className="flex flex-col items-center px-6 pb-12 pt-16 text-center md:px-8 md:pt-12">
          <Typography variant="header" size="h1" as="h1" align="center">
            {content.title}
          </Typography>

          <img src={content.illustrationSrc} alt="" className="mt-8 h-auto w-full max-w-sm" />

          <div className="mt-8">
            <Typography variant="body" size="medium" as="p" align="center">
              {content.supportPrefix}
              <Link href={supportHref}>Contact support</Link>
              {content.supportSuffix}
            </Typography>
          </div>
        </div>
      </ScreenCard>
    </PageLayout>
  );
}
